import React, { useEffect, useRef, useState } from 'react';

interface CountdownOverlayProps {
  isVisible: boolean;
  onComplete: () => void;
  countdownSeconds?: number;
}

export const CountdownOverlay: React.FC<CountdownOverlayProps> = ({
  isVisible,
  onComplete,
  countdownSeconds = 3
}) => {
  const [count, setCount] = useState(countdownSeconds);
  const [showGo, setShowGo] = useState(false);
  const completedRef = useRef(false);

  useEffect(() => {
    if (!isVisible) {
      setCount(countdownSeconds);
      setShowGo(false);
      completedRef.current = false;
      return;
    }

    setCount(countdownSeconds);
    setShowGo(false);
    completedRef.current = false;

    let goTimeout: number | undefined;

    const countdownInterval = setInterval(() => {
      setCount((prev) => {
        if (prev <= 1) {
          clearInterval(countdownInterval);
          setShowGo(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // Schedule the completion callback for when the countdown would reach 0
    // (countdownSeconds * 1s for the ticks + 500ms for the "GO!" display)
    goTimeout = window.setTimeout(() => {
      if (!completedRef.current) {
        completedRef.current = true;
        onComplete();
      }
    }, countdownSeconds * 1000 + 500);

    return () => {
      clearInterval(countdownInterval);
      if (goTimeout !== undefined) clearTimeout(goTimeout);
    };
  }, [isVisible, countdownSeconds, onComplete]);

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50">
      <div className="text-center">
        {!showGo ? (
          <div
            className="text-white font-bold animate-ping-once"
            style={{
              fontSize: '15rem',
              lineHeight: '1',
              textShadow: '0 0 40px rgba(139, 92, 246, 0.8), 0 0 80px rgba(139, 92, 246, 0.4)',
              animation: 'countdownPulse 1s ease-out'
            }}
          >
            {count}
          </div>
        ) : (
          <div
            className="text-kod-accent font-bold"
            style={{
              fontSize: '10rem',
              lineHeight: '1',
              textShadow: '0 0 40px rgba(139, 92, 246, 1), 0 0 80px rgba(139, 92, 246, 0.6)',
              animation: 'goAnimation 0.5s ease-out'
            }}
          >
            RECORD!
          </div>
        )}
      </div>
      <style>{`
        @keyframes countdownPulse {
          0% {
            transform: scale(0.8);
            opacity: 0;
          }
          50% {
            transform: scale(1.2);
            opacity: 1;
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }

        @keyframes goAnimation {
          0% {
            transform: scale(0.5);
            opacity: 0;
          }
          50% {
            transform: scale(1.1);
            opacity: 1;
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};
